// src/hooks/useReportQueries.js
// React Query hooks for the Reports & Analytics page
// Follows the same pattern as useWorkshopQueries.js

import { useQuery, useInfiniteQuery  } from "@tanstack/react-query";
import api from "../axiosInstance";

// ─── QUERY KEYS ──────────────────────────────────────────────────────────────
export const REPORT_KEYS = {
  overviewKPIs:     (range) => ["report-overview-kpis",     range],
  overviewMonthly:  (range) => ["report-overview-monthly",  range],
  overviewStages:   (range) => ["report-overview-stages",   range],
  overviewVehicles: (range) => ["report-overview-vehicles", range],
};

// ─── Overview: KPI Cards ──────────────────────────────────────────────────────
// Powers the 8 KPI cards at the top of the reports page.
// GET /api/admin/reports/overview/kpis?range=Last+6+months

export function useOverviewKPIs(range = "Last 6 months") {
  return useQuery({
    queryKey: REPORT_KEYS.overviewKPIs(range),
    queryFn: async () => {
      const { data } = await api.get("/api/admin/reports/overview/kpis", {
        params: { range },
      });

      const d = data?.data || {};
      return {
        totalCases:    Number(d.totalCases)    || 0,
        activeCases:   Number(d.activeCases)   || 0,
        settled:       Number(d.settled)       || 0,
        rejected:      Number(d.rejected)      || 0,
        totalRevenue:  Number(d.totalRevenue)  || 0,
        avgCycleDays:  Number(d.avgCycleDays)  || 0,
        slaBreachRate: Number(d.slaBreachRate) || 0,
        // avgRating not in schema yet — returns 0 until ratings table added
        avgRating:     Number(d.avgRating)     || 0,
      };
    },
  });
}

// ─── Overview: Monthly Chart Data ────────────────────────────────────────────
// Powers the bar chart, line chart, and summary table in the overview tab.
// GET /api/admin/reports/overview/monthly?range=Last+6+months

export function useOverviewMonthly(range = "Last 6 months") {
  return useQuery({
    queryKey: REPORT_KEYS.overviewMonthly(range),
    queryFn: async () => {
      const { data } = await api.get("/api/admin/reports/overview/monthly", {
        params: { range },
      });

      const raw = data?.data || [];
      return raw.map((r) => ({
        month:    r.month,
        cases:    Number(r.cases)    || 0,
        settled:  Number(r.settled)  || 0,
        rejected: Number(r.rejected) || 0,
        revenue:  Number(r.revenue)  || 0,
      }));
    },
  });
}

// ─── Overview: Stage Distribution ────────────────────────────────────────────
// Powers the donut chart showing active cases per stage.
// GET /api/admin/reports/overview/stages?range=Last+6+months

export function useOverviewStages(range = "Last 6 months") {
  return useQuery({
    queryKey: REPORT_KEYS.overviewStages(range),
    queryFn: async () => {
      const { data } = await api.get("/api/admin/reports/overview/stages", {
        params: { range },
      });

      const raw = data?.data || [];
      return raw.map((r) => ({
        stage: r.stage,
        count: Number(r.count) || 0,
        color: r.color,
      }));
    },
  });
}

// ─── Overview: Vehicle Type Breakdown ────────────────────────────────────────
// Powers the horizontal bar chart for vehicle types.
// GET /api/admin/reports/overview/vehicles?range=Last+6+months
// NOTE: returns zeros until vehicleType column is added to accident_cases

export function useOverviewVehicles(range = "Last 6 months") {
  return useQuery({
    queryKey: REPORT_KEYS.overviewVehicles(range),
    queryFn: async () => {
      const { data } = await api.get("/api/admin/reports/overview/vehicles", {
        params: { range },
      });

      const raw = data?.data || [];
      return raw.map((r) => ({
        type:  r.type,
        count: Number(r.count) || 0,
        color: r.color,
      }));
    },
  });
}

export function useWorkshopStats(range = "Last 6 months") {
  return useInfiniteQuery({
    queryKey: ["report-workshop-stats", range],
    queryFn: async ({ pageParam = null }) => {
      const { data } = await api.get("/api/admin/reports/workshops/stats", {
        params: { range, cursor: pageParam, limit: 10  },
      });
      return data;
    },
    getNextPageParam: (lastPage) => lastPage.nextCursor ?? undefined,
    initialPageParam: null,
  });
}
 
// ─── Workshop: Monthly Trend ──────────────────────────────────────────────────
// Monthly case counts for the top 5 workshops.
// GET /api/admin/reports/workshops/trend?range=Last+6+months
 
export function useWorkshopCasesTrend(range = "Last 6 months") {
  return useQuery({
    queryKey: ["report-workshop-trend", range],
    queryFn: async () => {
      const { data } = await api.get("/api/admin/reports/workshops/trend", {
        params: { range },
      });
      return data?.data || [];
    },
  });
}

export function useSurveyorStats(range = "Last 6 months") {
  return useInfiniteQuery({
    queryKey: ["report-surveyor-stats", range],
    queryFn: async ({ pageParam = null }) => {
      const { data } = await api.get("/api/admin/reports/surveyors/stats", {
        params: { range, cursor: pageParam, limit: 10 },
      });
      return data;
    },
    getNextPageParam: (lastPage) => lastPage.nextCursor ?? undefined,
    initialPageParam: null,
  });
}
 
// ─── Surveyor: Monthly Trend ──────────────────────────────────────────────────
// Monthly survey counts for the top 5 surveyors.
// GET /api/admin/reports/surveyors/trend?range=Last+6+months
 
export function useSurveyorTrend(range = "Last 6 months") {
  return useQuery({
    queryKey: ["report-surveyor-trend", range],
    queryFn: async () => {
      const { data } = await api.get("/api/admin/reports/surveyors/trend", {
        params: { range },
      });
      return data?.data || [];
    },
  });
}

export function useClaimsSummary(range = "Last 6 months") {
  return useQuery({
    queryKey: ["report-claims-summary", range],
    queryFn: async () => {
      const { data } = await api.get("/api/admin/reports/claims/summary", {
        params: { range },
      });
      return data; 
    },
  });
}

export function useSLASummary(range = "Last 6 months") {
  return useQuery({
    queryKey: ["report-sla-summary", range],
    queryFn: async () => {
      const { data } = await api.get("/api/admin/reports/sla/summary", {
        params: { range },
      });
      return data; // { success, data: { monthly, stages } }
    },
  });
}

export function useDailyReport(filters = {}) {
  const {
    range    = "Last 6 months",
    status   = "ALL",
    category,
    clientId,   // ← NEW
    search,
    type,       // ← NEW  "client" | "vehicle" | "customer"
  } = filters;
 
  return useInfiniteQuery({
    // include clientId + type in queryKey so cache is per-filter
    queryKey: ["report-daily", range, status, category, clientId, search, type],
    queryFn: async ({ pageParam = null }) => {
      const params = { range, status, limit: 50 };
 
      if (category && category !== "ALL") params.category = category;
 
      // ── Search params — mirror what the backend expects ───────────────────
      if (clientId) {
        // Fast path: exact FK match — no text search needed
        params.clientId = clientId;
      } else if (search?.trim()) {
        params.search = search.trim();
        if (type) params.type = type;
      }
 
      if (pageParam !== null) params.cursor = pageParam;
 
      const { data } = await api.get("/api/admin/reports/daily-report", { params });
 
      const rows = (data?.data || []).map((r) => ({
        sr:               r.sr,
        vehicleNumber:    r.vehicleNumber    || "",
        accidentCaseNo:   r.accidentCaseNo   || "",
        customerName:     r.customerName     || "",
        clientName:       r.clientName       || "",
        category:         r.category         || "MAJOR",
        makeModel:        r.makeModel        || "",
        year:             r.year             || null,
        bodyType:         r.bodyType         || "",
        placeOfAccident:  r.placeOfAccident  || "",
        dateOfAccident:   r.dateOfAccident   || null,
        insuranceType:    r.insuranceType    || "COMPREHENSIVE",
        rootCause:        r.rootCause        || "",
        briefDescription: r.briefDescription || "",
        idvValue:         r.idvValue         != null ? Number(r.idvValue) : null,
        workshopName:     r.workshopName     || "",
        workshopPlace:    r.workshopPlace    || "",
        updateAsOn:       r.updateAsOn       || "",
        eta:              r.eta              || null,
        claimSettledDate: r.claimSettledDate || null,
        daysLapsed:       Number(r.daysLapsed) || 0,
        autoStatus:       r.autoStatus       || "ACTIVE",
        daysBucket:       r.daysBucket       || "Unknown",
      }));
 
      return {
        data:       rows,
        summary:    data?.summary    || null,
        total:      data?.total      || rows.length,
        nextCursor: data?.nextCursor ?? null,
      };
    },
    getNextPageParam: (lastPage) => lastPage.nextCursor ?? undefined,
    initialPageParam: null,
    staleTime: 60_000,
  });
}

export function useReportSearch(query = "",type = "client") {
  return useQuery({
    queryKey: ["report-search", query, type],
    queryFn: async () => {
      if (!query.trim()) return [];
      const { data } = await api.get("/api/admin/reports/search", {
        params: { q: query.trim(), type, limit: 8 },
      });
      return data?.data || [];
    },
    enabled: query.trim().length >= 2,
    staleTime: 30_000,
  });
}

export function useReportExportCases(filters = {}, enabled = false) {
  const { clientId,clientName, vehicleNumber, customerName } = filters;

  return useQuery({
    queryKey: ["report-export-cases", filters],
    queryFn: async () => {
      const params = {};
      if (clientId)       params.clientId = clientId;
      if (clientName)    params.clientName    = clientName;
      if (vehicleNumber) params.vehicleNumber = vehicleNumber;
      if (customerName)  params.customerName  = customerName;

      const { data } = await api.get("/api/admin/reports/export-cases", { params });
      console.log("Export cases fetched from API:------------------", data);

      const rows = (data?.data || []).map((r, i) => ({
        sr:             i + 1,
        category:       r.category        || "MAJOR",
        vehicleNumber:  r.vehicleNumber   || "",
        makeModel:      r.makeModel       || "",
        year:           r.year            || null,
        bodyType:       r.bodyType        || "",
        placeOfAccident:r.placeOfAccident || "",
        dateOfAccident: r.dateOfAccident  || null,
        rootCause:      r.rootCause       || "",
        briefDescription: r.briefDescription || "",
        insuranceType: r.insuranceType   || "COMPREHENSIVE",
        idvValue:       r.idvValue       != null ? Number(r.idvValue) : null,
        workshopName:   r.workshopName    || "",
        workshopPlace:  r.workshopPlace   || "",
        updateAsOn:     r.updateAsOn      || "",
        eta:            r.eta             || null,
        claimSettledDate: r.claimSettledDate || null,
        daysLapsed:     Number(r.daysLapsed) || 0,
        workshopEstimate:  r.workshopEstimate != null ? Number(r.workshopEstimate) : null,
        approvedBySurveyor: r.approvedBySurveyor != null ? Number(r.approvedBySurveyor) : null,
        currentStatus:  r.currentStatus   || r.briefDescription || "",
        detailedUpdate: r.detailedUpdate  || r.briefDescription || "-",
        notes:          r.notes           || r.rootCause        || "-",
        finalStatus:    r.finalStatus     || r.autoStatus       || "ACTIVE",
        autoStatus:     r.autoStatus      || "ACTIVE",
        deprecation:       r.depreciation       != null ? Number(r.depreciation) : null,
        imt23:             r.imt23             != null ? Number(r.imt23) : null,
        consumable:       r.consumable       != null ? Number(r.consumable) : null,
        salvage:          r.salvage          != null ? Number(r.salvage) : null,
        fileCharge:       r.fileCharge       != null ? Number(r.fileCharge) : null,
        workshopPayment:  r.workshopPayment  != null ? Number(r.workshopPayment) : null,
        claimStatus: r.claimStatus || null,
      }));
      console.log("Export cases fetched:", rows);
 
      return {
         clientName:  data?.clientName  || clientName || "Client",
        clientShort: data?.clientShort || (clientName
          ? clientName.split(" ").map(w => w[0]).join("").slice(0, 6)
          : "CLIENT"),
        cases: rows,
      };
    },
    enabled: enabled && !!(clientId || clientName || vehicleNumber || customerName),
    staleTime: 0,
  });
}